import { forwardRef } from "react";

import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useId } from "../../hooks/useId";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { CheckboxProps } from "./Checkbox";
import { Placeholder } from "./Placeholder";
import { InputContainer } from "../shared";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";
import { SharedCheckbox } from "./SharedCheckbox";
import { AccessibilityProps } from "../../AccessibilityProps";

const useResolvedCheckboxStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "checkbox",
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends CheckboxProps, Pick<AccessibilityProps, "aria-label"> {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * The main checkbox component. This component is not exported to the outside world. This component is used inside the components that are being exported.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const { defaultValue = null, instructions, hideLabel, hideInstructions } = props;
  const { value = defaultValue } = props;

  const id = useId();

  const checked = value === true;
  const indeterminate = value === "indeterminate";
  const { mode, styles, StyleModeBoundary } = useResolvedCheckboxStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Checkbox,
      "checkbox",
      {
        indeterminate,
        checked,
      },
    ],
    testIdPrefix: "checkbox",
  });

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder />}>
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          instructions={instructions}
          tooltip={undefined}
          required={undefined}
          hideLabel={hideLabel}
          hideInstructions={hideInstructions}
        >
          <SharedCheckbox {...props} id={id} ref={ref} />
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
